<script setup lang="ts">
import type { RailTrain } from '@/@types/rail';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import EstimatedTime from '@/components/ui/EstimatedTime.vue';
import Tag from '@/components/ui/InfoTag.vue';
import { railTrainIcon, type RailTrainTime } from '@/libs/rail-helper';
import { TagValues } from '@/libs/tags-helper';

defineProps<{
  train: RailTrain;
  /** Read off the arrival or the departure, depending on the board the card is on. */
  time: RailTrainTime;
}>();
</script>

<template>
  <div class="train-card" :class="{ 'train-card--canceled': time.isCanceled }">
    <div class="train-card__info">
      <div class="train-card__identity">
        <div class="train-card__destination">
          <BaseIcon :name="railTrainIcon(train)" class="train-card__icon" />
          <span class="train-card__route">{{ train.route_long_name }}</span>
        </div>
        <div class="train-card__train">
          <span v-if="train.trip_headsign" class="train-card__headsign">
            {{ train.trip_headsign }}
          </span>
        </div>
      </div>

      <EstimatedTime
        class="train-card__time"
        :delay="time.delayMinutes !== undefined ? $t('trains.delay', { n: time.delayMinutes }) : undefined"
        :secondary-time="time.scheduledTime"
        :time="time.time"
        :type="time.type"
      />
    </div>

    <div v-if="time.isCanceled" class="train-card__tags">
      <Tag :label="$t('trains.canceled')" :tag-type="TagValues.CANCELLED" />
    </div>
  </div>
</template>

<style lang="scss">
.train-card {
  background-color: $color-neutral-lower;
  border-radius: $radius-3xl;
  display: flex;
  flex-direction: column;
  gap: $spacing-2;
  padding: $spacing-4;

  &__info {
    display: flex;
    gap: $spacing-3;
  }

  &__identity {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-1;
    justify-content: center;
    min-width: 0;
  }

  &__destination {
    align-items: center;
    display: flex;
    gap: 6px;
    min-width: 0;
  }

  &__icon {
    color: $color-neutral-highest;
    font-size: 20px;
  }

  // A long route name is clipped rather than pushing the time out
  &__route {
    color: $color-neutral-highest;
    flex: 1;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__train {
    display: flex;
    font: $label-medium;
    gap: $spacing-2;
    letter-spacing: $label-medium-spacing;
    white-space: nowrap;
  }

  &__headsign {
    color: $color-neutral-higher;
  }

  &__tags {
    display: flex;
    gap: $spacing-2;
  }

  // A cancelled train keeps its timetable readable, struck through, on a greyed card
  &--canceled {
    .train-card__icon,
    .train-card__route {
      color: $color-neutral-higher;
    }

    .train-card__time .estimated-time__text {
      color: $color-neutral-higher;
      text-decoration: line-through;
    }
  }
}
</style>
